import {
  Text,
  VStack,
  Flex,
  Box,
  Grid,
  Button,
  ResponsiveValue,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next'

import ScrollLink from '@/lib/scroll'

import { GET_QUOTATION } from '@/routes/landing'

import { NAVIGATION_BAR_HEIGHT } from '@/components/NavigationBar/constants'

export interface HeroProps {
  title: React.ReactNode
  subtitle: React.ReactNode
  image: ResponsiveValue<string>
  colorSchemePrimary?: string
  titleColor?: string
  subtitleColor?: string
  bgColor?: string
  onClickGetQuote?: () => void
}

function Hero({
  title,
  subtitle,
  image,
  titleColor,
  subtitleColor,
  colorSchemePrimary,
  bgColor,
  onClickGetQuote,
}: HeroProps): React.ReactElement {
  const { t } = useTranslation('services')

  return (
    <Flex
      as="section"
      id="hero"
      sx={{
        w: 'full',
        minH: {
          base: `calc(100vh - ${NAVIGATION_BAR_HEIGHT.base})`,
          md: `calc(100vh - ${NAVIGATION_BAR_HEIGHT.md})`,
        },
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bgColor,
        backgroundImage: image,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        position: 'relative',
        mixBlendMode: 'multiply',
        backgroundSize: {
          base: 'cover',
          md: 'auto 100%',
        },
      }}
    >
      <Grid
        layerStyle="container"
        sx={{
          gridTemplateColumns: {
            base: '1fr',
            md: '5fr 4fr',
          },
          gridGap: {
            base: 6,
            md: 0,
          },
          alignItems: 'center',
        }}
      >
        <VStack
          spacing="4"
          sx={{
            fontWeight: 'medium',
            alignItems: {
              base: 'center',
              md: 'flex-start',
            },
            color: 'white',
            textAlign: {
              base: 'center',
              md: 'left',
            },
          }}
        >
          <Text
            as="h1"
            sx={{
              color: titleColor,
              fontSize: {
                base: '3.5xl',
                sm: '5xl',
                md: '5.5xl',
              },
              whiteSpace: {
                base: 'unset',
                md: 'pre',
              },
              lineHeight: 1.2,
              '& > br': {
                display: {
                  base: 'none',
                  md: 'block',
                },
              },
            }}
          >
            {title}
          </Text>
          <Text
            as="h2"
            sx={{
              color: subtitleColor,
              fontSize: {
                base: 'xl',
                sm: '3.5xl',
              },
            }}
          >
            {subtitle}
          </Text>
          <Box
            sx={{
              pt: {
                base: 8,
                sm: 12,
              },
            }}
          >
            <Button
              as={ScrollLink}
              url={{ ...GET_QUOTATION, pathname: 'currentPath' }}
              onClick={onClickGetQuote}
              colorScheme={colorSchemePrimary}
              size="xl"
              sx={{
                cursor: 'pointer',
              }}
            >
              {t('common.cta.getQuotation')}
            </Button>
          </Box>
        </VStack>
      </Grid>
    </Flex>
  )
}

export default Hero
